/**
 * Commerce · Customer Purchase Signals.
 */
import { useMemo, useState } from "react";
import {
  Sparkles, ShoppingCart, Clock3, TrendingUp, AlertTriangle, Layers, Crown, Signal,
} from "lucide-react";
import { Card, SectionHeader, Button, TextInput, ConfidenceBar } from "../../components/lib";
import { TabBar, ListRow, Drawer, Field } from "../../components/lib.overlay";
import { useDeepLink, RelatedLinksBlock } from "../../lib/integration";
import { linksForSignalCustomer } from "../../mocks/entityLinks";
import { SIGNALS, SignalKind, PurchaseSignal } from "../../mocks/commerceData";

/* ── Signals meta ─────────────────────────────────────────────── */
const KIND_META: Record<SignalKind, { icon: any; label: string; tint: string; fg: string }> = {
  "cart-abandon":   { icon: ShoppingCart, label: "Cart abandon",   tint: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"        },
  "reorder-window": { icon: Clock3,        label: "Reorder window", tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  "high-ltv":       { icon: TrendingUp,    label: "High LTV",        tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  "at-risk":        { icon: AlertTriangle, label: "At risk",         tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  "cross-sell":     { icon: Layers,        label: "Cross-sell",      tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  "vip":            { icon: Crown,         label: "VIP",             tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
};

export default function Signals() {
  const { q: initialQ } = useDeepLink();
  const [tab, setTab] = useState<SignalKind | "all">("all");
  const [q, setQ]     = useState(initialQ);
  const [selected, setSelected] = useState<PurchaseSignal | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: SIGNALS.length };
    SIGNALS.forEach((s) => { c[s.kind] = (c[s.kind] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => SIGNALS.filter((s) =>
      (tab === "all" || s.kind === tab) &&
      (!q || s.customer.toLowerCase().includes(q.toLowerCase()) || s.detail.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, q],
  );

  return (
    <div className="space-y-6" data-testid="cm-signals">
      <Card testId="cm-signals-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search by customer or context…" testId="cm-signals-search" /></div>
          <span className="text-[11px] inline-flex items-center gap-1" style={{ color: "var(--ph-ai)" }}><Sparkles size={11} /> AI · updated 4 min ago</span>
        </div>
        <div className="mt-4">
          <TabBar<SignalKind | "all">
            testId="cm-signals-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",            label: "All",            count: counts.all            },
              { key: "vip",            label: "VIP",            count: counts.vip            || 0 },
              { key: "high-ltv",       label: "High LTV",       count: counts["high-ltv"]    || 0 },
              { key: "reorder-window", label: "Reorder",        count: counts["reorder-window"] || 0 },
              { key: "cart-abandon",   label: "Cart abandon",   count: counts["cart-abandon"] || 0 },
              { key: "cross-sell",     label: "Cross-sell",     count: counts["cross-sell"]  || 0 },
              { key: "at-risk",        label: "At risk",        count: counts["at-risk"]     || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="cm-signals-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Feed" title={`${filtered.length} signal${filtered.length === 1 ? "" : "s"}`} action={<Signal size={14} style={{ color: "var(--ph-ink-muted)" }} />} />
        </div>
        <div className="p-2" data-testid="cm-signals-list">
          {filtered.map((s) => {
            const meta = KIND_META[s.kind];
            const Icon = meta.icon;
            return (
              <ListRow
                key={s.id}
                testId={`cm-signal-${s.id}`}
                onClick={() => setSelected(s)}
                icon={
                  <span className="inline-flex items-center justify-center" style={{
                    width: 28, height: 28, borderRadius: "999px",
                    background: meta.tint, color: meta.fg,
                  }}>
                    <Icon size={13} />
                  </span>
                }
                label={
                  <span className="inline-flex items-center gap-2">
                    <span>{s.customer}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {meta.label}
                    </span>
                  </span>
                }
                sublabel={<>{s.city} · LTV EGP {s.ltv.toLocaleString()} · last order {s.lastOrderRelative} · <span style={{ color: "var(--ph-ink-default)" }}>{s.detail}</span></>}
                trailing={
                  <div style={{ width: 80 }}>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-right" style={{ color: "var(--ph-ai)" }}>AI {Math.round(s.aiConfidence * 100)}%</div>
                    <div className="mt-1"><ConfidenceBar value={s.aiConfidence} /></div>
                  </div>
                }
              />
            );
          })}
        </div>
      </Card>

      <Drawer open={Boolean(selected)} onClose={() => setSelected(null)} title="Signal" testId="cm-signal-drawer" width={460}>
        {selected && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: KIND_META[selected.kind].fg }}>
              {KIND_META[selected.kind].label}
            </div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.25rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">
              {selected.customer} · {selected.city}
            </div>
            <div className="text-sm mb-3" style={{ color: "var(--ph-ink-default)" }}>{selected.detail}</div>
            <Field label="Lifetime value" inline>
              <span className="text-sm tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>EGP {selected.ltv.toLocaleString()}</span>
            </Field>
            <Field label="Last order" inline><span className="text-sm">{selected.lastOrderRelative}</span></Field>
            <Field label="AI confidence" inline>
              <div style={{ width: 120 }}>
                <div className="text-[11px] mb-1 tabular-nums" style={{ color: "var(--ph-ai)" }}>{Math.round(selected.aiConfidence * 100)}%</div>
                <ConfidenceBar value={selected.aiConfidence} />
              </div>
            </Field>
            <div className="pt-3">
              <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ink-muted)" }}>Suggested next action</div>
              <div className="text-sm p-3" style={{ background: "var(--ph-surface)", borderRadius: "var(--ph-radius-md)", color: "var(--ph-ink-default)" }}>
                {selected.suggestedAction}
              </div>
            </div>
            <RelatedLinksBlock links={linksForSignalCustomer(selected.customer)} testId="cm-signal-related" />
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" testId="cm-signal-dismiss">Dismiss</Button>
              <Button testId="cm-signal-act">Run action</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
